import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  DatePicker,
  Select,
  Space,
  Tag,
  message,
  Modal,
  Tooltip,
  Typography,
  Row,
  Col,
  Divider
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  CopyOutlined,
  StopOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import EmployeeSelect from '../components/common/EmployeeSelect';
import massSendService from '../services/massSendService';
import weworkService from '../services/weworkService';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

const CustomerMassSendList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `共 ${total} 条记录，显示第 ${range[0]}-${range[1]} 条`
  });

  // 搜索条件
  const [searchParams, setSearchParams] = useState({
    taskName: '',
    status: undefined,
    createdBy: null,
    dateRange: null
  });

  const [employees, setEmployees] = useState([]);

  // 任务状态配置
  const statusConfig = {
    draft: { text: '草稿', color: 'default' },
    pending: { text: '待执行', color: 'orange' },
    sending: { text: '发送中', color: 'blue' },
    completed: { text: '已完成', color: 'green' },
    failed: { text: '失败', color: 'red' },
    cancelled: { text: '已取消', color: 'volcano' }
  };

  // 发送类型配置
  const sendTypeConfig = {
    IMMEDIATE: { text: '立即发送', color: 'green' },
    SCHEDULED: { text: '定时发送', color: 'blue' }
  };

  // 加载数据
  const loadTasks = async (params = {}) => {
    try {
      setLoading(true);

      const queryParams = {
        page: (params.current || pagination.current) - 1,
        size: params.pageSize || pagination.pageSize,
        taskType: 'CUSTOMER', // 只查询客户群发任务
        taskName: searchParams.taskName,
        status: searchParams.status,
        createdBy: searchParams.createdBy,
        startDate: searchParams.dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: searchParams.dateRange?.[1]?.format('YYYY-MM-DD')
      };

      // 过滤空值
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key]) {
          delete queryParams[key];
        }
      });

      const response = await massSendService.getTaskList(queryParams);

      if (response.success && response.data) {
        setTasks(response.data.content || []);
        setPagination(prev => ({
          ...prev,
          current: (response.data.currentPage || 0) + 1,
          total: response.data.totalElements || 0,
          pageSize: response.data.size || 20
        }));
      }
    } catch (error) {
      console.error('加载任务列表失败:', error);
      message.error('加载任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载员工列表
  const loadEmployees = async () => {
    try {
      const employeeList = await weworkService.getAllEmployees();
      setEmployees(employeeList?.filter(emp => emp.systemStatus === 1) || []);
    } catch (error) {
      console.error('加载员工列表失败:', error);
    }
  };

  useEffect(() => {
    loadTasks();
    loadEmployees();
  }, []);

  // 搜索处理
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadTasks({ current: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      taskName: '',
      status: '',
      createdBy: null,
      dateRange: null
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    // 延迟加载以确保状态更新
    setTimeout(() => {
      loadTasks({ current: 1 });
    }, 100);
  };

  // 创建新任务
  const handleCreate = () => {
    navigate('/customer-mass-send/create');
  };

  // 复制任务
  const handleCopy = async (record) => {
    try {
      // 获取任务详情
      const taskDetail = await massSendService.getTaskById(record.id);
      if (taskDetail.success && taskDetail.data) {
        const originalTask = taskDetail.data;

        // 创建复制的任务数据
        const copyTaskData = {
          ...originalTask,
          taskName: `${originalTask.taskName}_副本_${moment().format('MMDD_HHmm')}`,
          status: 'draft',
          id: undefined,
          createdAt: undefined,
          updatedAt: undefined
        };

        // 跳转到创建页面并传递数据
        navigate('/customer-mass-send/create', {
          state: {
            copyFrom: copyTaskData
          }
        });
      }
    } catch (error) {
      console.error('复制任务失败:', error);
      message.error('复制任务失败');
    }
  };

  // 终止任务
  const handleStop = (record) => {
    confirm({
      title: '确认终止任务',
      content: `确定要终止任务"${record.taskName}"吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await massSendService.cancelTask(record.id);
          message.success('任务已终止');
          loadTasks();
        } catch (error) {
          console.error('终止任务失败:', error);
          message.error('终止任务失败');
        }
      }
    });
  };

  // 查看详情
  const handleView = (record) => {
    navigate(`/customer-mass-send/view/${record.id}`);
  };


  // 删除任务
  const handleDelete = (record) => {
    confirm({
      title: '确认删除任务',
      content: `确定要删除任务"${record.taskName}"吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await massSendService.deleteTask(record.id);
          message.success('任务已删除');
          loadTasks();
        } catch (error) {
          console.error('删除任务失败:', error);
          message.error('删除任务失败');
        }
      }
    });
  };

  // 表格列配置
  const columns = [
    {
      title: '名称',
      dataIndex: 'taskName',
      key: 'taskName',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: '发送内容',
      dataIndex: 'textContent',
      key: 'textContent',
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        // 构建显示内容
        let content = text || '';

        // 解析附件并添加描述
        if (record.attachments) {
          try {
            const attachments = JSON.parse(record.attachments);
            if (Array.isArray(attachments) && attachments.length > 0) {
              const attachmentDescriptions = attachments.map(att => {
                switch (att.type) {
                  case 'IMAGE_GROUP': return '图片';
                  case 'VIDEO': return '视频';
                  case 'LINK': return '链接';
                  case 'MINIPROGRAM': return '小程序';
                  case 'FILE': return '文件';
                  default: return '附件';
                }
              });

              if (content) {
                content += ` [${attachmentDescriptions.join('、')}]`;
              } else {
                content = `[${attachmentDescriptions.join('、')}]`;
              }
            }
          } catch (e) {
            // 解析失败时忽略
          }
        }

        const displayContent = content || '空内容';
        return (
          <Tooltip title={displayContent}>
            <span>{displayContent}</span>
          </Tooltip>
        );
      }
    },
    {
      title: '发送类型',
      dataIndex: 'sendTimeType',
      key: 'sendTimeType',
      width: 100,
      render: (type) => {
        const config = sendTypeConfig[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '发送时间',
      dataIndex: 'scheduledTime',
      key: 'scheduledTime',
      width: 150,
      render: (time, record) => {
        if (record.sendTimeType === 'IMMEDIATE') {
          return record.createdAt ? moment(record.createdAt).format('YYYY-MM-DD HH:mm') : '-';
        }
        return time ? moment(time).format('YYYY-MM-DD HH:mm') : '-';
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = statusConfig[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '创建人',
      dataIndex: 'createdByName',
      key: 'createdByName',
      width: 100
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (time) => time ? moment(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: 125,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleCopy(record)}
          >
            复制
          </Button>
          {(record.status === 'pending' || record.status === 'sending') && (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => handleStop(record)}
            >
              终止
            </Button>
          )}
          {(record.status === 'completed' || record.status === 'failed' || record.status === 'cancelled') && (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => handleDelete(record)}
            >
              删除
            </Button>
          )}
        </Space>
      )
    }
  ];

  // 表格变化处理
  const handleTableChange = (paginationConfig, filters, sorter) => {
    const params = {
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    };

    if (sorter && sorter.field) {
      params.sortBy = sorter.field;
      params.sortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
    }

    loadTasks(params);
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>客户群发</Title>
        </Col>
      </Row>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input
                  placeholder="搜索任务名称"
                  value={searchParams.taskName}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, taskName: e.target.value }))}
                  onPressEnter={handleSearch}
                  allowClear
                  style={{ width: 200 }}
                />
                <Select
                  placeholder="选择状态"
                  value={searchParams.status}
                  onChange={(value) => setSearchParams(prev => ({ ...prev, status: value }))}
                  allowClear
                  style={{ width: 120 }}
                >
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <Option key={key} value={key}>{config.text}</Option>
                  ))}
                </Select>
                <EmployeeSelect
                  placeholder="选择创建人"
                  value={searchParams.createdBy}
                  onChange={(value) => setSearchParams(prev => ({ ...prev, createdBy: value }))}
                  allowClear
                  style={{ width: 150 }}
                  showDisplayModeSwitch={false}
                />
                <RangePicker
                  placeholder={['开始日期', '结束日期']}
                  value={searchParams.dateRange}
                  onChange={(dates) => setSearchParams(prev => ({ ...prev, dateRange: dates }))}
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  搜索
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  创建客户群发
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={tasks}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default CustomerMassSendList; 